/**
 * Auto-battle resolution — pure logic, no Phaser, no DOM.
 *
 * The game loop calls these each tick. Functions mutate the passed state and
 * return an array of events the renderer/UI subscribe to (damage numbers,
 * deaths, drops). All tunables come from the injected data tables.
 *
 * Combat is a party of 1-2 heroes versus 1-3 monsters (a boss fights solo).
 * Resolution is focus-fire: every living hero swings at the same target (the
 * first living monster), and each living monster retaliates against a random
 * living hero. Events carry `heroIndex` / `monsterIndex` so the renderer can
 * animate the right sprite.
 */

import { equipmentStatTotals } from './items.js';
import { rollDrops, rollGuaranteedEquipment } from './loot.js';
import { addEquipment, addFood, addReviveKit, addProtectionAmulet, autoEat, useReviveKit, dungeonRemainingMs } from './inventory.js';
import { xpToNext } from './progression.js';

/**
 * @typedef {object} CombatData
 * @property {object} classes parsed classes.json
 * @property {object} maps parsed maps.json
 * @property {object} [loot] parsed loot.json
 * @property {object} [balance] parsed balance.json
 */

/**
 * Derive a hero's effective combat stats from class base + level growth +
 * equipped item bonuses. The `hp` stat on items adds to maxHp.
 * @param {object} hero a party member from state
 * @param {object} classes classes.json
 * @param {Record<string, object|null>} [equipped] equipped instances by slot
 * @param {object} [skillsData] parsed skills.json — if provided, applies hero.skills effects
 * @returns {{maxHp:number, atk:number, mag:number, def:number, res:number, crit:number, critDmg:number, atkSpd:number, dodge:number}}
 */
export function heroDerivedStats(hero, classes, equipped, skillsData) {
  const cls = classes[hero.classId];
  const base = cls.base;
  const g = cls.growth ?? {};
  const lv = hero.level - 1;

  const stats = {
    maxHp: Math.round(base.hp + (g.hp ?? 0) * lv),
    atk: base.atk + (g.atk ?? 0) * lv,
    mag: base.mag + (g.mag ?? 0) * lv,
    def: base.def + (g.def ?? 0) * lv,
    res: base.res + (g.res ?? 0) * lv,
    crit: base.crit,
    critDmg: base.critDmg,
    atkSpd: base.atkSpd,
    dodge: base.dodge,
    // Active combat effects granted by skills (and, later, gear). All default to
    // 0 so summing skill effects "just works" via the (stat in stats) check.
    lifesteal: 0, // heal this fraction of damage dealt
    burn: 0,      // bonus on-hit fire damage as a fraction of the hit
    thorns: 0,    // reflect this fraction of incoming damage to the attacker
    regen: 0,     // extra HP regen per round as a fraction of maxHp (self)
    regenAura: 0, // healing aura: heals every party member per round (priest)
    poison: 0,    // applies a stacking damage-over-time to the target on hit
  };

  if (equipped) {
    for (const item of Object.values(equipped)) {
      if (!item) continue;
      const totals = equipmentStatTotals(item);
      for (const [stat, value] of Object.entries(totals)) {
        if (stat === 'hp') stats.maxHp += value;
        else if (stat in stats) stats[stat] += value;
      }
    }
    stats.maxHp = Math.round(stats.maxHp);
  }

  if (skillsData && hero.skills?.length) {
    const classSkills = skillsData[hero.classId] ?? [];
    const learnedSet = new Set(hero.skills);
    for (const skill of classSkills) {
      if (!learnedSet.has(skill.id)) continue;
      for (const [stat, value] of Object.entries(skill.effect)) {
        if (stat in stats) stats[stat] += value;
      }
    }
    stats.maxHp = Math.round(stats.maxHp);
  }

  // Percent-based stats can stack from skills AND up to 8 gear affixes with no
  // natural ceiling — uncapped, a heavily-geared character could reach 100%
  // crit/dodge (removing all RNG from combat) or absurd on-hit effect values.
  // Clamp each to a sane maximum so itemization stays additive-but-bounded.
  stats.crit = Math.min(stats.crit, 0.75);
  stats.dodge = Math.min(stats.dodge, 0.6);
  stats.atkSpd = Math.min(stats.atkSpd, 3.0);
  stats.lifesteal = Math.min(stats.lifesteal, 0.5);
  stats.burn = Math.min(stats.burn, 0.5);
  stats.thorns = Math.min(stats.thorns, 0.5);
  stats.poison = Math.min(stats.poison, 0.5);
  stats.regen = Math.min(stats.regen, 0.1);
  stats.regenAura = Math.min(stats.regenAura, 0.1);

  return stats;
}

/**
 * Compute a single physical hit.
 * damage = ATK * (100/(100+DEF)) * critMult * variance(0.9..1.1)
 * @param {number} atk
 * @param {number} def
 * @param {number} crit chance 0..1
 * @param {number} critDmg extra crit multiplier (e.g. 0.5 = +50%)
 * @param {import('./rng.js').Rng} rng
 * @returns {{damage:number, crit:boolean}}
 */
export function physicalHit(atk, def, crit, critDmg, rng) {
  const mitigation = 100 / (100 + Math.max(0, def));
  const isCrit = rng.chance(crit);
  const critMult = isCrit ? 1 + critDmg : 1;
  const variance = rng.range(0.9, 1.1);
  const damage = Math.max(1, Math.round(atk * mitigation * critMult * variance));
  return { damage, crit: isCrit };
}

/**
 * Pick the active map definition.
 * @param {object} state
 * @param {object} maps maps.json
 * @returns {object}
 */
export function activeMap(state, maps) {
  return maps[state.activeMapId] ?? maps[Object.keys(maps)[0]];
}

/**
 * In-zone difficulty scaling: monsters grow with the party so a zone stays
 * dangerous through its WHOLE level range. Without this, monster stats are
 * static while heroes nearly double across the zone — at the top of the range
 * everything died in one hit and food/revives never got spent. progress is the
 * party's strongest level clamped into the map's levelRange.
 * @param {object} map active map def
 * @param {object[]} party
 * @param {object} [balance] balance.json (zoneScaling block)
 * @returns {{hp:number, atk:number, rew:number}}
 */
export function zoneScaleMult(map, party, balance) {
  const cfg = balance?.zoneScaling;
  if (!cfg) return { hp: 1, atk: 1, rew: 1 };
  // The tier-1 nursery is exempt: brand-new gearless heroes shouldn't meet
  // scaled-up wolves. From minTier on, zones harden as the party grows.
  if ((map.tier ?? 1) < (cfg.minTier ?? 1)) return { hp: 1, atk: 1, rew: 1 };
  const [lo, hi] = map.levelRange ?? [1, 1];
  const maxLevel = Math.max(1, ...(party ?? []).map((h) => h.level ?? 1));
  const progress = Math.max(0, Math.min(maxLevel, hi) - lo);
  return {
    hp: 1 + (cfg.hpPerLevel ?? 0) * progress,
    atk: 1 + (cfg.atkPerLevel ?? 0) * progress,
    rew: 1 + (cfg.rewardPerLevel ?? 0) * progress,
  };
}

/**
 * Instantiate a live monster from a map template.
 * @param {object} tmpl monster template from maps.json
 * @param {boolean} isBoss
 * @param {object} [elite] elite config { statMult } when this spawn is an elite
 * @param {{hp:number, atk:number, rew:number}} [zone] in-zone scaling (zoneScaleMult)
 */
function spawnFromTemplate(tmpl, isBoss, elite, zone) {
  // Elite variant: a rare, tougher version that can appear on any map (never a
  // boss). Stats scale up by statMult; the renderer marks it with a ★ and it
  // rolls drops twice as often (see awardDrops).
  const m = elite ? (elite.statMult ?? 1.6) : 1;
  const z = zone ?? { hp: 1, atk: 1, rew: 1 };
  return {
    id: tmpl.id,
    label: tmpl.label,
    isBoss: !!isBoss,
    isElite: !!elite,
    hp: Math.round(tmpl.hp * m * z.hp),
    maxHp: Math.round(tmpl.hp * m * z.hp),
    atk: Math.round(tmpl.atk * m * z.atk),
    def: Math.round(tmpl.def * m),
    xp: Math.round(tmpl.xp * m * z.rew),
    gold: [Math.round(tmpl.gold[0] * (elite ? m : 1) * z.rew), Math.round(tmpl.gold[1] * (elite ? m : 1) * z.rew)],
    // Optional on-attack abilities (e.g. boss venom/lifedrain): { poison, lifesteal }.
    mods: tmpl.mods ? { ...tmpl.mods } : null,
  };
}

/**
 * Kills required before the map's boss appears. Scales per map tier so the boss
 * gap widens as you climb: 100 on tier 1, 200 on tier 2, 300 on tier 3, … The
 * per-map `boss.everyKills` (default 100) is the tier-1 base that gets scaled.
 * @param {object} map active map def
 * @returns {number}
 */
export function bossKillThreshold(map) {
  const base = map?.boss?.everyKills ?? 100;
  const tier = Math.max(1, map?.tier ?? 1);
  return base * tier;
}

/**
 * Ensure a pack of monsters is present on the active map; spawn one if the
 * current pack is empty/cleared. A boss challenge spawns a single boss;
 * otherwise 1..maxMonsters regular monsters appear.
 * @param {object} state
 * @param {CombatData} data
 * @param {import('./rng.js').Rng} rng
 * @returns {object[]} events ([{type:'spawn', monsters}]) or []
 */
export function ensureMonsters(state, data, rng) {
  const pack = state.currentMonsters ?? [];
  if (pack.some((m) => m && m.hp > 0)) return [];

  // A daily dungeon run in progress fights ONLY its own (tougher) solo boss —
  // it never falls back to the active map's regular pack/boss rotation.
  if (state.dungeonRun) {
    const dungeon = data.dungeons?.[state.dungeonRun.dungeonId];
    if (dungeon) {
      const monsters = [spawnFromTemplate(dungeon.boss, true)];
      state.currentMonsters = monsters;
      return [{ type: 'spawn', monsters }];
    }
    // Dungeon def missing/removed — bail out of the run rather than getting stuck.
    state.dungeonRun = null;
  }

  const map = activeMap(state, data.maps);
  // A boss appears automatically once enough regular monsters have been slain on
  // the map (map.boss.everyKills), or on demand via state.bossChallenge (PORTAL).
  // It fights alone; the kill counter resets when it spawns.
  const bossDue = !!map.boss && (state.killsSinceBoss ?? 0) >= bossKillThreshold(map);
  const isBoss = (!!state.bossChallenge || bossDue) && !!map.boss;
  if (state.bossChallenge) state.bossChallenge = false;

  // In-zone scaling: the pack (and the boss) grow with the party's level so
  // the zone stays a real fight all the way to the top of its range.
  const zone = zoneScaleMult(map, state.party, data.balance);

  let monsters;
  if (isBoss) {
    monsters = [spawnFromTemplate(map.boss, true, null, zone)];
    state.killsSinceBoss = 0;
  } else {
    // Scale pack size to party size: every living monster attacks EVERY round
    // (there's no "one monster at a time" pacing), so a solo hero facing the
    // same up-to-3 pack a duo faces eats 2-3x the incoming damage with half the
    // HP pool to soak it — that was killing fresh solo characters in minutes.
    const partySize = Math.max(1, (state.party ?? []).length);
    const configuredMax = Math.max(1, data.balance?.combat?.maxMonsters ?? 1);
    const maxMonsters = Math.max(1, Math.min(configuredMax, partySize + 1));
    const count = rng.int(1, maxMonsters);
    // Rare elite roll (per monster): a tougher ★ variant that can show up on any
    // map. Config lives in balance.json (chance ≈ 0.05 → ~5 per 100 monsters).
    const eliteCfg = data.balance?.elite;
    monsters = [];
    for (let i = 0; i < count; i++) {
      const elite = eliteCfg && rng.chance(eliteCfg.chance ?? 0) ? eliteCfg : null;
      monsters.push(spawnFromTemplate(rng.pick(map.monsters), false, elite, zone));
    }
  }

  state.currentMonsters = monsters;
  return [{ type: 'spawn', monsters }];
}

/**
 * Apply per-tick HP regen to a hero, scaling with level.
 * @param {object} hero
 * @param {object} hs derived hero stats
 * @param {object} balance balance.json
 * @returns {number} hp restored
 */
export function applyRegen(hero, hs, balance) {
  if (!hero.alive || hero.hp >= hs.maxHp) return 0;
  const r = balance.regen;
  // Base regen scales with level; skills (e.g. priest) add a flat % on top.
  const pct = r.basePctPerTick + r.perLevelPctPerTick * (hero.level - 1) + (hs.regen ?? 0);
  const heal = Math.max(1, Math.round(hs.maxHp * pct));
  const before = hero.hp;
  hero.hp = Math.min(hs.maxHp, hero.hp + heal);
  return hero.hp - before;
}

/** Index of the first living monster in the pack, or -1 if the pack is clear. */
function firstLivingMonster(monsters) {
  return monsters.findIndex((m) => m && m.hp > 0);
}

/**
 * Resolve one combat round (focus-fire):
 *  - upkeep per hero (regen + auto-eat),
 *  - each living hero swings at the current focus target (first living monster),
 *    awarding xp/gold/drops to the killer on a kill and moving to the next target,
 *  - each living monster retaliates against a random living hero.
 * @param {object} state
 * @param {CombatData} data
 * @param {import('./rng.js').Rng} rng
 * @returns {object[]} ordered combat events
 */
export function resolveRound(state, data, rng) {
  /** @type {object[]} */
  const events = [];
  const party = state.party ?? [];
  const map = activeMap(state, data.maps);

  // Cache derived stats per hero and run upkeep (regen + auto-eat).
  const statsByHero = party.map((hero) => {
    const hs = heroDerivedStats(hero, data.classes, hero.equipped, data.skills);
    hero.maxHp = hs.maxHp;
    if (hero.alive) {
      if (data.balance) applyRegen(hero, hs, data.balance);
      if (data.loot && data.balance) {
        const ate = autoEat(state, hero, data);
        if (ate) events.push({ type: 'food_eat', heroIndex: party.indexOf(hero), foodId: ate.foodId, heal: ate.heal, heroHp: hero.hp, heroMaxHp: hs.maxHp });
      }
    }
    return hs;
  });

  // Healing aura: a priest's aura heals every living party member each round
  // (stacks if both heroes carry one). Applied after self-regen during upkeep.
  const auraPct = statsByHero.reduce((s, hs, i) => s + (party[i]?.alive ? (hs.regenAura ?? 0) : 0), 0);
  if (auraPct > 0) {
    for (let i = 0; i < party.length; i++) {
      const hero = party[i];
      if (!hero?.alive || hero.hp >= statsByHero[i].maxHp) continue;
      const heal = Math.max(1, Math.round(statsByHero[i].maxHp * auraPct));
      const before = hero.hp;
      hero.hp = Math.min(statsByHero[i].maxHp, hero.hp + heal);
      if (hero.hp > before) {
        events.push({ type: 'aura_heal', heroIndex: i, heal: hero.hp - before, heroHp: hero.hp, heroMaxHp: statsByHero[i].maxHp });
      }
    }
  }

  // Venom on heroes (from boss attacks) ticks during upkeep and can down them.
  for (let i = 0; i < party.length; i++) {
    const hero = party[i];
    if (!hero?.alive || !hero.poison) continue;
    const dmg = hero.poison.dmg;
    hero.hp = Math.max(0, hero.hp - dmg);
    events.push({ type: 'hero_poison_tick', heroIndex: i, damage: dmg, heroHp: hero.hp, heroMaxHp: statsByHero[i].maxHp });
    hero.poison.turns -= 1;
    if (hero.poison.turns <= 0) hero.poison = null;
    if (hero.hp <= 0) {
      hero.poison = null;
      events.push(...handleDeath(state, data, hero, i));
    }
  }

  events.push(...ensureMonsters(state, data, rng));
  const monsters = state.currentMonsters ?? [];

  const anyHeroAlive = party.some((h) => h.alive);
  if (!monsters.length || !anyHeroAlive) return events;

  // Under-level penalty: fighting in a zone above your level is brutal. A hero
  // whose level is below the map's minimum takes amplified damage and deals
  // reduced damage, so you must level up before climbing tiers.
  const lp = data.balance?.levelPenalty;
  const mapMinLevel = map.levelRange?.[0] ?? 1;
  const incomingMult = (hero) => {
    if (!lp) return 1;
    const deficit = Math.max(0, mapMinLevel - (hero.level ?? 1));
    return Math.min(lp.maxIncoming ?? 99, 1 + (lp.incomingPerLevel ?? 0) * deficit);
  };
  const outgoingMult = (hero) => {
    if (!lp) return 1;
    const deficit = Math.max(0, mapMinLevel - (hero.level ?? 1));
    return Math.max(lp.minOutgoing ?? 0, 1 - (lp.outgoingPerLevel ?? 0) * deficit);
  };

  // ---- Poison ticks: damage-over-time applied last round now bites. It can
  // kill, and the kill is credited to whoever applied it (falling back to any
  // living hero) so xp/gold/drops are never lost. ----
  const firstLivingHero = () => party.findIndex((h) => h?.alive);
  for (let m = 0; m < monsters.length; m++) {
    const mon = monsters[m];
    if (!mon || mon.hp <= 0 || !mon.poison) continue;
    const dmg = mon.poison.dmg;
    mon.hp = Math.max(0, mon.hp - dmg);
    events.push({ type: 'poison_tick', monsterIndex: m, damage: dmg, targetHp: mon.hp, targetMaxHp: mon.maxHp });
    const by = mon.poison.by;
    mon.poison.turns -= 1;
    if (mon.poison.turns <= 0) mon.poison = null;
    if (mon.hp <= 0) {
      const ki = party[by]?.alive ? by : firstLivingHero();
      if (ki !== -1) events.push(...grantKill(state, data, party[ki], ki, mon, m, statsByHero[ki], rng));
    }
  }
  if (firstLivingMonster(monsters) === -1) {
    state.currentMonsters = [];
    return events;
  }

  // ---- Heroes strike: every living hero focuses the first living monster. ----
  for (let h = 0; h < party.length; h++) {
    const hero = party[h];
    if (!hero.alive) continue;
    const hs = statsByHero[h];

    const ti = firstLivingMonster(monsters);
    if (ti === -1) break; // pack cleared
    const target = monsters[ti];

    const dmgMod = (data.balance?.heroDamageMod ?? 1) * outgoingMult(hero);
    // Power is whichever offensive stat the class actually uses — physical
    // classes carry atk (mag=0), casters (pyromancer/priest) carry mag (their
    // atk is a small fallback). Using atk alone left casters doing almost no
    // damage regardless of how much mag their staff/wand/gear granted.
    const power = Math.max(hs.atk, hs.mag);
    const hit = physicalHit(power * hs.atkSpd * dmgMod, target.def, hs.crit, hs.critDmg, rng);
    // Burn: a fraction of the hit is added as bonus fire damage (same strike, so
    // the kill is still credited to this hero — no DoT bookkeeping needed).
    const burnDmg = hs.burn > 0 ? Math.round(hit.damage * hs.burn) : 0;
    const totalDmg = hit.damage + burnDmg;
    target.hp = Math.max(0, target.hp - totalDmg);
    events.push({ type: 'hero_attack', heroIndex: h, targetIndex: ti, damage: totalDmg, crit: hit.crit, burn: burnDmg, targetHp: target.hp, targetMaxHp: target.maxHp });

    // Lifesteal: heal the attacker for a fraction of the damage dealt.
    if (hs.lifesteal > 0 && hero.hp > 0) {
      const healed = Math.round(totalDmg * hs.lifesteal);
      if (healed > 0) {
        hero.hp = Math.min(hs.maxHp, hero.hp + healed);
        events.push({ type: 'lifesteal', heroIndex: h, heal: healed, heroHp: hero.hp, heroMaxHp: hs.maxHp });
      }
    }

    if (target.hp <= 0) {
      events.push(...grantKill(state, data, hero, h, target, ti, hs, rng));
    } else if (hs.poison > 0) {
      // Poison: apply/refresh a stacking damage-over-time on the target. The
      // stronger stack wins; re-hitting refreshes its duration. It ticks at the
      // start of the next round (see the poison phase above).
      const pd = Math.max(1, Math.round(hit.damage * hs.poison));
      if (!target.poison || pd >= target.poison.dmg) target.poison = { dmg: pd, turns: 3, by: h };
      else target.poison.turns = 3;
    }
  }

  // If the whole pack is down, clear it so the next round spawns fresh.
  if (firstLivingMonster(monsters) === -1) {
    state.currentMonsters = [];
    return events;
  }

  // ---- Monsters retaliate: each living monster hits a random living hero. ----
  for (let m = 0; m < monsters.length; m++) {
    const monster = monsters[m];
    if (!monster || monster.hp <= 0) continue;

    const livingHeroes = party.map((h, i) => ({ h, i })).filter((o) => o.h.alive);
    if (!livingHeroes.length) break;
    // Front-to-back targeting: melee classes (knight/rogue) form the front line
    // and soak hits first. Back-line heroes (pyromancer/ranger/priest) only get
    // attacked once every front-liner is down — that's what "tanking" means.
    const frontline = livingHeroes.filter((o) => data.classes[o.h.classId]?.frontline);
    const pool = frontline.length ? frontline : livingHeroes;
    const pickIdx = rng.int(0, pool.length - 1);
    const { h: hero, i: hi } = pool[pickIdx];
    const hs = statsByHero[hi];

    if (rng.chance(hs.dodge)) {
      events.push({ type: 'hero_dodge', heroIndex: hi, monsterIndex: m });
    } else {
      // res (magic resist) had no source of magic damage to mitigate — it was a
      // dead stat on every caster and every res-affix item. Fold it into general
      // mitigation alongside def so it's never wasted itemization.
      const back = physicalHit(monster.atk * incomingMult(hero), hs.def + hs.res, 0, 0, rng);
      // Protection Amulet: consumed on dungeon entry, cuts incoming damage for
      // that whole run — the paid/rare hedge against losing the day's one shot.
      if (state.dungeonRun?.protected) back.damage = Math.round(back.damage * 0.7);
      hero.hp = Math.max(0, hero.hp - back.damage);
      events.push({ type: 'monster_attack', monsterIndex: m, heroIndex: hi, damage: back.damage, heroHp: hero.hp, heroMaxHp: hs.maxHp });

      // Monster on-attack abilities (bosses): drain HP and/or inflict venom.
      if (monster.mods && back.damage > 0) {
        if (monster.mods.lifesteal > 0 && monster.hp > 0) {
          const drained = Math.round(back.damage * monster.mods.lifesteal);
          if (drained > 0) {
            monster.hp = Math.min(monster.maxHp, monster.hp + drained);
            events.push({ type: 'monster_lifesteal', monsterIndex: m, heal: drained });
          }
        }
        if (monster.mods.poison > 0 && hero.hp > 0) {
          const pd = Math.max(1, Math.round(back.damage * monster.mods.poison));
          if (!hero.poison || pd >= hero.poison.dmg) hero.poison = { dmg: pd, turns: 3 };
          else hero.poison.turns = 3;
        }
      }

      // Thorns: reflect a fraction of the damage taken back at the attacker.
      if (hs.thorns > 0 && monster.hp > 0) {
        const reflect = Math.round(back.damage * hs.thorns);
        if (reflect > 0) {
          monster.hp = Math.max(0, monster.hp - reflect);
          events.push({ type: 'thorns', monsterIndex: m, heroIndex: hi, damage: reflect, targetHp: monster.hp, targetMaxHp: monster.maxHp });
        }
      }

      if (hero.hp <= 0) events.push(...handleDeath(state, data, hero, hi));
    }
  }

  return events;
}

/**
 * Award a kill to a hero: gold, xp, kill counters, on-kill heal, then drops and
 * level-ups. Shared by direct strikes and damage-over-time (poison) kills so the
 * credit, loot and progression are identical no matter what landed the blow.
 * @returns {object[]} kill + drop + level-up events
 */
function grantKill(state, data, hero, heroIndex, target, targetIndex, hs, rng) {
  const events = [];
  const rawGold = rng.int(target.gold[0], target.gold[1]);
  const goldGain = Math.max(1, Math.round(rawGold * (data.balance?.goldMod ?? 1)));
  state.player.gold += goldGain;
  state.stats.totalGold += goldGain;
  state.stats.kills += 1;
  if (!target.isBoss) state.killsSinceBoss = (state.killsSinceBoss ?? 0) + 1;
  const xpGain = Math.max(1, Math.round(target.xp * (data.balance?.xpMod ?? 1)));
  hero.xp += xpGain;
  // Heal the killer a chunk on kill so easy zones stay sustainable.
  const healPct = data.balance?.healOnKillPct ?? 0;
  if (healPct > 0 && hs) hero.hp = Math.min(hs.maxHp, hero.hp + Math.round(hs.maxHp * healPct));
  events.push({ type: 'kill', heroIndex, monsterIndex: targetIndex, monster: target.id, isBoss: target.isBoss, xp: xpGain, gold: goldGain });

  // Daily dungeon: killing its boss clears the run and grants the guaranteed
  // reward instead of (on top of) the normal RNG-gated drop roll — the whole
  // point of a dungeon is a SURE thing at the end, win or lose the day's shot.
  if (state.dungeonRun && target.isBoss) {
    const dungeon = data.dungeons?.[state.dungeonRun.dungeonId];
    if (dungeon) {
      const usableBy = [...new Set((state.party ?? []).map((h) => h.classId))];
      const item = rollGuaranteedEquipment(dungeon.guaranteedRarity, hero.level, usableBy, data, rng);
      if (item) {
        addEquipment(state, item);
        events.push({ type: 'drop_equipment', heroIndex, item });
      }
    }
    state.dungeonRun = null;
    events.push({ type: 'dungeon_clear', heroIndex });
  } else {
    events.push(...awardDrops(state, data, target, hero, heroIndex, rng));
  }

  events.push(...applyLevelUps(state, data, hero, heroIndex));
  return events;
}

/**
 * Roll and grant drops for a kill, returning drop events. Drops go to the
 * killing hero (item level scales off that hero).
 * @param {object} state
 * @param {CombatData} data
 * @param {object} monster the slain monster
 * @param {object} hero the killer
 * @param {number} heroIndex
 * @param {import('./rng.js').Rng} rng
 * @returns {object[]}
 */
export function awardDrops(state, data, monster, hero, heroIndex, rng) {
  if (!data.loot) return [];
  const events = [];
  const map = activeMap(state, data.maps);
  // Bias equipment drops toward gear someone in the party can actually equip.
  const usableBy = [...new Set((state.party ?? []).map((h) => h.classId))];
  // Elites drop twice as often (balance.json elite.dropMult) — folded into the
  // map's dropMod so every drop roll (equipment/food/kit/amulet) benefits.
  const eliteDropMult = monster.isElite ? (data.balance?.elite?.dropMult ?? 1) : 1;
  const drops = rollDrops(
    { isBoss: monster.isBoss, itemLevel: hero.level, dropMod: (map.dropMod ?? 1) * eliteDropMult, usableBy, mapId: state.activeMapId },
    data,
    rng,
  );
  if (drops.equipment) {
    addEquipment(state, drops.equipment);
    events.push({ type: 'drop_equipment', heroIndex, item: drops.equipment });
  }
  if (drops.food) {
    // Some maps boost a food's drop quantity (e.g. green_meadows: 2× strawberry).
    const qty = map.foodQtyMods?.[drops.food.foodId] ?? 1;
    addFood(state, drops.food.foodId, qty);
    events.push({ type: 'drop_food', heroIndex, food: drops.food, qty });
  }
  if (drops.reviveKit) {
    addReviveKit(state, 1);
    events.push({ type: 'drop_revive', heroIndex });
  }
  if (drops.protectionAmulet) {
    addProtectionAmulet(state, 1);
    events.push({ type: 'drop_protection', heroIndex });
  }
  return events;
}

/**
 * Apply any pending level-ups to a hero after XP gain. Each level recomputes
 * stats (class growth raises them) and fully heals the hero.
 * @param {object} state
 * @param {CombatData} data
 * @param {object} hero
 * @param {number} heroIndex
 * @returns {object[]} level_up events
 */
export function applyLevelUps(state, data, hero, heroIndex) {
  const cfg = data.balance?.progression;
  if (!cfg) return [];
  const events = [];
  let guard = 0;
  while (hero.xp >= xpToNext(hero.level, cfg) && guard++ < 999) {
    hero.xp -= xpToNext(hero.level, cfg);
    hero.level += 1;
    hero.skillPoints = (hero.skillPoints ?? 0) + 1;
    const hs = heroDerivedStats(hero, data.classes, hero.equipped, data.skills);
    hero.maxHp = hs.maxHp;
    hero.hp = hs.maxHp; // reward: full heal on level up
    events.push({ type: 'level_up', heroIndex, level: hero.level });
  }
  return events;
}

/**
 * Handle a hero hitting 0 HP: spend a revive kit if owned, otherwise down the
 * hero and start its individual death lockout.
 * @param {object} state
 * @param {CombatData} data
 * @param {object} hero
 * @param {number} heroIndex
 * @returns {object[]}
 */
export function handleDeath(state, data, hero, heroIndex) {
  if (useReviveKit(state, hero)) {
    return [{ type: 'hero_revived_kit', heroIndex }];
  }
  hero.alive = false;
  hero.reviveAt = 0;
  state.stats.deaths = (state.stats.deaths ?? 0) + 1;
  const hours = data.balance?.death?.lockoutHours ?? 2;
  hero.lockedUntil = Date.now() + hours * 3600 * 1000;
  const events = [{ type: 'hero_down', heroIndex, lockedUntil: hero.lockedUntil }];

  // A real death (no kit saved them) fails the day's dungeon attempt outright —
  // it was a one-shot gamble, not a grind you can just keep retrying today.
  if (state.dungeonRun) {
    state.dungeonRun = null;
    state.currentMonsters = [];
    events.push({ type: 'dungeon_fail', heroIndex });
  }

  return events;
}

/**
 * Revive any downed hero whose individual death lockout has elapsed, at full HP.
 * @param {object} state
 * @param {object} classes classes.json
 * @returns {object[]} events
 */
export function handleRevives(state, classes) {
  const events = [];
  const party = state.party ?? [];
  for (let i = 0; i < party.length; i++) {
    const hero = party[i];
    if (!hero.alive && (hero.lockedUntil ?? 0) <= Date.now()) {
      const hs = heroDerivedStats(hero, classes, hero.equipped);
      hero.maxHp = hs.maxHp;
      hero.hp = hs.maxHp;
      hero.alive = true;
      hero.reviveAt = 0;
      hero.lockedUntil = 0;
      events.push({ type: 'hero_revive', heroIndex: i });
    }
  }
  return events;
}
